import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { Session, Klass, PLAYING, PAUSED, FINISHED } from 'state';
import { classCode, classNewestFirst, relativeAgo } from './helpers';

type Props = {
  klasses: Klass[];
  sessions: Session[];
  selectedId: number;
  onSelect: (id: number) => void;
};

type ClassMeta = {
  count: number;
  hasRunning: boolean;
  hasPaused: boolean;
  hasInProgress: boolean;
};

const ClassRail: React.FC<Props> = ({
  klasses,
  sessions,
  selectedId,
  onSelect,
}) => {
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);

  // Per-class indicators derived from the (already loaded) full session list.
  const metaByClass = useMemo(() => {
    const map: Record<number, ClassMeta> = {};
    for (const s of sessions) {
      const m = (map[s.klass] = map[s.klass] || {
        count: 0,
        hasRunning: false,
        hasPaused: false,
        hasInProgress: false,
      });
      m.count += 1;
      if (s.status === PLAYING) m.hasRunning = true;
      if (s.status === PAUSED) m.hasPaused = true;
      if (s.status !== FINISHED) m.hasInProgress = true;
    }
    return map;
  }, [sessions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return klasses
      .filter(c => {
        if (q && !classCode(c).toLowerCase().includes(q)) return false;
        if (activeOnly && !metaByClass[c.id]?.hasInProgress) return false;
        return true;
      })
      .sort(classNewestFirst);
  }, [klasses, search, activeOnly, metaByClass]);

  // Keep the selected row visible WITHIN the rail list — both when the
  // selection changes and when filtering re-positions it. Scoped to the list
  // container so it never scrolls the whole page (which scrollIntoView would).
  const activeRowRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = activeRowRef.current;
    const list = listRef.current;
    if (!row || !list) return;
    const rowRect = row.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (rowRect.top < listRect.top) {
      list.scrollTop -= listRect.top - rowRect.top;
    } else if (rowRect.bottom > listRect.bottom) {
      list.scrollTop += rowRect.bottom - listRect.bottom;
    }
  }, [selectedId, filtered]);

  return (
    <aside className="td-rail">
      <div className="td-rail-head">
        <div className="td-top">
          <strong>Classes</strong>
          <span className="td-count">{klasses.length}</span>
        </div>
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined />}
          placeholder="Search classes…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search classes"
        />
        <div className="td-chips">
          <button
            type="button"
            className={`td-chip ${activeOnly ? 'is-on' : ''}`}
            aria-pressed={activeOnly}
            onClick={() => setActiveOnly(v => !v)}
          >
            {activeOnly ? '● ' : ''}In progress only
          </button>
        </div>
      </div>

      <div className="td-rail-list" ref={listRef}>
        {filtered.length ? (
          filtered.map(c => {
            const code = classCode(c);
            const m = metaByClass[c.id];
            const n = m?.count ?? 0;
            const sel = c.id === selectedId;
            const running = !!m?.hasRunning;
            // Amber "paused" only for genuinely paused classes — transient
            // Creating/Delay/Frozen states get no colored indicator (they are
            // still surfaced in the hero and the "In progress only" filter).
            const pausedOnly = !running && !!m?.hasPaused;
            return (
              <button
                type="button"
                key={c.id}
                ref={sel ? activeRowRef : undefined}
                className={`td-citem ${sel ? 'is-sel' : ''}`}
                title={code}
                onClick={() => onSelect(c.id)}
              >
                <div className="td-cname td-truncate">{code}</div>
                <div className="td-csub">
                  <span>
                    {n} session{n !== 1 ? 's' : ''}
                  </span>
                  <span>·</span>
                  <span>{relativeAgo(c.start_date as string)}</span>
                  {running ? (
                    <span className="td-live-tag">
                      <span className="td-dot" />
                      live
                    </span>
                  ) : pausedOnly ? (
                    <span className="td-live-tag is-paused">
                      <span className="td-dot td-dot--amber" />
                      paused
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })
        ) : (
          <div className="td-rail-empty">
            {search ? `No classes match “${search}”.` : 'No classes to show.'}
          </div>
        )}
      </div>
    </aside>
  );
};

export default ClassRail;
